import React from "react";
import { ChartType, LegendKey } from "../../../../models/chart_model";
import { normalizeDisplayLabel } from "../../../../utility/displayText";
import {
  getLegendColor,
  getLegendColorKey,
  isLegendDashed,
} from "../utils/colorUtils";

interface LegendItemProps {
  legend: LegendKey;
  index: number;
  selectedLegends: LegendKey[];
  handleLegendClick: (legend: LegendKey) => void;
  circleOnly?: boolean;
  fontSize?: number;
  colorMap?: Record<string, string>;
  isSmallLegend?: boolean;
}

const LegendItemBase: React.FC<LegendItemProps> = ({
  legend,
  index,
  selectedLegends,
  handleLegendClick,
  circleOnly,
  fontSize = 12,
  colorMap,
  isSmallLegend = false,
}) => {
  const isSelected =
    selectedLegends.some((item) => item.dataKey === legend.dataKey) ||
    selectedLegends.length === 0;
  const legendColorKey = getLegendColorKey(legend, index);
  const dashed = isLegendDashed(legend);
  // colorMap is the resolved authority — it already absorbed any predefined
  // legendColor/stroke and fixed forecast color collisions.
  const color = isSelected
    ? colorMap?.[legendColorKey] ||
      legend.legendColor ||
      legend.stroke ||
      getLegendColor(legend, index, selectedLegends, true)
    : "#E0E0E0";

  return (
    <div
      key={legend.dataKey}
      className={"line-legend"}
      style={{ fontSize: fontSize.toString() + `px` }}
      onClick={() => handleLegendClick(legend)}
    >
      <div style={{ color }}>
        <div
          className={
            legend.type === ChartType.Bar || circleOnly
              ? "dot"
              : legend.type === ChartType.Step
                ? "step"
                : dashed
                  ? "dash"
                  : "line"
          }
          style={{
            marginRight: "6px",
            backgroundColor:
              legend.type === ChartType.Step || dashed
                ? "transparent"
                : color,
            fontSize: isSmallLegend ? "10px!important" : undefined,
            width:
              isSmallLegend && legend.type !== ChartType.Bar
                ? "10px"
                : undefined,
            borderColor: color,
          }}
        ></div>
        {normalizeDisplayLabel(legend.name)}
      </div>
    </div>
  );
};

export const LegendItem = React.memo(LegendItemBase);

/**
 * Backward-compat function form. Existing code calls
 * `LegendComponent(legend, index, ...)` — keep that signature working.
 * New code should prefer `<LegendItem ... />`.
 */
export const LegendComponent = (
  legend: LegendKey,
  index: number,
  selectedLegends: LegendKey[],
  handleLegendClick: (legend: LegendKey) => void,
  circleOnly?: boolean,
  fontSize = 12,
  colorMap?: Record<string, string>,
  isSmallLegend = false,
) => (
  <LegendItem
    legend={legend}
    index={index}
    selectedLegends={selectedLegends}
    handleLegendClick={handleLegendClick}
    circleOnly={circleOnly}
    fontSize={fontSize}
    colorMap={colorMap}
    isSmallLegend={isSmallLegend}
  />
);
